import { InputAdornment, TextField } from '@mui/material';
import { flexStyle, rgba, textStyle } from '@themes/mixins';
import styled, { css } from 'styled-components';

import Icon from '../Icon';

import { Sizes } from './enums';
import { Props } from './types';

export const Root = styled.div<Partial<Props>>`
  ${flexStyle({
    align: 'flex-start',
    direction: 'column',
    justify: 'flex-start',
    wrap: 'nowrap',
  })};
`;
export const Label = styled.label<{ error?: boolean }>`
  ${flexStyle({ align: 'center', wrap: 'nowrap' })};
  ${textStyle({
    color: 'textPrimary',
    lineHeight: 1,
    size: '12px',
    textAlign: 'left',
    weight: 400,
  })};
  margin-bottom: 8px;
  letter-spacing: 0.5px;

  ${({ error, theme }) =>
    error &&
    css`
      color: ${theme.colors.red};
    `}
`;
export const RequiredMark = styled.span<{ error?: boolean }>`
  ${textStyle({
    color: 'textPrimary',
    lineHeight: 1,
    size: '12px',
    textAlign: 'left',
    weight: 400,
  })};
  margin-left: 5px;

  ${({ error, theme }) =>
    error &&
    css`
      color: ${theme.colors.red};
    `}
`;
export const ClearAdornmentWrapper = styled.div`
  padding-right: 6px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
`;

export const InputContainer = styled.div<Partial<Props>>`
  ${flexStyle({ align: 'center', wrap: 'nowrap' })};
  width: 100%;
  background-color: ${({ theme }) => theme.colors.darkSurfacePrimary};

  ${({ disabled, theme }) =>
    disabled &&
    css`
      cursor: not-allowed;

      &:hover {
        border: 0 solid ${theme.colors.divider};
      }
    `}
`;

export const StyledTextField = styled(TextField)<{
  sizetype: Sizes;
  $isSuccess: boolean;
}>`
  & label {
    color: ${({ theme }) => theme.colors.textSecondary};

    ${({ sizetype }) => {
      switch (sizetype) {
        case Sizes.small:
          return css`
            font-size: 14px;
            top: -10px;
          `;
        case Sizes.medium:
          return css`
            top: -4px;
          `;
        default:
          return css``;
      }
    }}
  }

  & label.Mui-focused {
    color: ${({ theme }) => theme.colors.textPrimary};
    top: 0;
  }

  & label.MuiFormLabel-filled {
    top: 0;
  }

  & .MuiInputLabel-formControl.Mui-disabled {
    color: ${({ theme }) => theme.colors.divider};
  }

  & .MuiOutlinedInput-root.Mui-disabled {
    & fieldset {
      border-color: ${({ theme }) => theme.colors.divider};
    }
  }

  & .MuiOutlinedInput-root.Mui-readOnly {
    background-color: ${({ theme }) => rgba(theme.colors.divider, 10)};
  }

  & .MuiInputLabel-formControl.Mui-error {
    color: ${({ theme }) => theme.colors.red};

    & .MuiInputLabel-asterisk.Mui-error {
      color: ${({ theme }) => theme.colors.red};
    }
  }

  & .MuiOutlinedInput-root {
    color: ${({ theme }) => theme.colors.textPrimary};
    -webkit-text-fill-color: ${({ theme }) => theme.colors.textPrimary};
    padding: 0;

    /* Override browser autofill yellow background and text color */

    & input:-webkit-autofill,
    & input:-webkit-autofill:hover,
    & input:-webkit-autofill:focus,
    & input:-webkit-autofill:active {
      -webkit-text-fill-color: ${({ theme }) => theme.colors.textPrimary};
      caret-color: ${({ theme }) => theme.colors.textPrimary};
      -webkit-box-shadow: 0 0 0 1000px ${({ theme }) => theme.colors.background}
        inset;
      box-shadow: 0 0 0 1000px ${({ theme }) => theme.colors.background} inset;
      transition: background-color 9999s ease-in-out 0s;
    }

    &.Mui-disabled {
      input {
        -webkit-text-fill-color: ${({ theme }) => theme.colors.textSecondary};
      }

      &:hover fieldset {
        border-color: ${({ theme }) => theme.colors.divider};
      }
    }

    & fieldset {
      border-color: ${({ theme, $isSuccess }) =>
        $isSuccess ? theme.colors.green : theme.colors.divider};

      ${({ $isSuccess }) =>
        $isSuccess &&
        css`
          background-color: ${({ theme }) => rgba(theme.colors.green, 0.1)};
        `}
    }

    &:hover fieldset {
      border-color: ${({ theme }) => theme.colors.textSecondary};
    }

    &.Mui-focused fieldset {
      border-color: ${({ theme }) => theme.colors.textSecondary};
    }

    /* Reserve space for the clear icon */

    .MuiOutlinedInput-input {
      ${({ sizetype }) => {
        switch (sizetype) {
          case Sizes.small:
            return css`
              padding: 6px 1px 6px 14px;
              font-size: 12px;
            `;
          case Sizes.medium:
            return css`
              padding: 12px 6px 12px 14px;
            `;
          default:
            return css``;
        }
      }}
    }

    .MuiOutlinedInput-input:-webkit-autofill,
    .MuiOutlinedInput-input:-webkit-autofill:hover,
    .MuiOutlinedInput-input:-webkit-autofill:active,
    .MuiOutlinedInput-input:-webkit-autofill:focus {
      -webkit-box-shadow: 0 0 0 1000px ${({ theme }) => theme.colors.background}
        inset !important;
      box-shadow: 0 0 0 1000px ${({ theme }) => theme.colors.background} inset !important;
      background-color: ${({ theme }) => theme.colors.background};
    }

    /* Show clear icon (X) on hover or focus */

    &:hover
      .MuiInputAdornment-root
      ${ClearAdornmentWrapper},
      &.Mui-focused
      .MuiInputAdornment-root
      ${ClearAdornmentWrapper} {
      opacity: 1;
      pointer-events: auto;
    }

    &.Mui-error {
      & .MuiOutlinedInput-input::placeholder {
        color: ${({ theme }) => theme.colors.red};
      }

      &.Mui-focused fieldset {
        border-color: ${({ theme }) => theme.colors.red};
      }

      & fieldset {
        color: ${({ theme }) => theme.colors.red};
      }

      &:hover fieldset {
        border-color: ${({ theme }) => theme.colors.red};
      }
    }
  }
`;

export const StyledInputAdornment = styled(InputAdornment)<{
  $error?: boolean;
}>`
  color: ${({ theme }) => theme.colors.textSecondary};

  ${({ $error, theme }) =>
    $error &&
    css`
      color: ${theme.colors.red};
    `}
`;
export const ErrorMessageContainer = styled.div<{
  sizetype: Sizes;
}>`
  ${flexStyle({ align: 'center', wrap: 'nowrap' })};
  ${({ sizetype }) => {
    switch (sizetype) {
      case Sizes.small:
        return css`
          margin-top: 2px;
        `;
      case Sizes.medium:
        return css`
          margin-top: 8px;
        `;
      default:
        return css`
          margin-top: 8px;
        `;
    }
  }}
`;
export const AttentionIcon = styled(Icon)`
  color: ${({ theme }) => theme.colors.red};
  margin-right: 4px;
`;
export const ErrorMessage = styled.span`
  ${textStyle({
    color: 'red',
    lineHeight: 1,
    size: '12px',
    textAlign: 'left',
    weight: 400,
  })};
  letter-spacing: 0.4px;
`;
export const LoadingAdornmentWrapper = styled.div`
  padding-right: 6px;
`;
export const StyledIcon = styled(Icon)`
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
`;
